import { useEffect, useState } from 'react';

import { Button, Dialog, Field, Input } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { loiTheoTruong } from '../../../lib/errors';
import { useNopLaiPhien } from '../hooks/useThamDinh';
import { ChonTepHoSo, DUOI_NHAN, TRAN_MOI_TEP } from './ChonTepHoSo';

export interface HopNopLaiProps {
  open: boolean;
  onClose: () => void;
  phienId: string;
  tieuDeCu: string;
  /** Mở màn chi tiết của phiên mới sau khi nộp xong. */
  onXong?: (phienId: string) => void;
}

/**
 * Nộp lại hồ sơ sau khi sửa (đặc tả mục 13.4, Mốc 9).
 *
 * Không có ô chọn bộ tiêu chí: nộp lại là nộp lại **theo cùng bộ**, backend lấy `bo_id` từ phiên trước — nộp
 * theo một bộ khác thì đó là hồ sơ khác, không phải lần nộp thứ hai.
 *
 * Tiêu đề mặc định là tiêu đề cũ kèm số lần nộp, để danh sách hồ sơ phân biệt được hai lần mà người dùng
 * không phải tự nghĩ tên.
 */
export function HopNopLai({ open, onClose, phienId, tieuDeCu, onXong }: HopNopLaiProps) {
  const { t } = useT();
  const dichLoi = useLoi();
  const nopLai = useNopLaiPhien();

  const [tieuDe, setTieuDe] = useState('');
  const [tep, setTep] = useState<File[]>([]);

  useEffect(() => {
    if (!open) return;
    setTieuDe(tieuDeCu);
    setTep([]);
    nopLai.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tieuDeCu]);

  const loiTruong = loiTheoTruong(nopLai.error);
  const loiChung = nopLai.error && Object.keys(loiTruong).length === 0 ? dichLoi(nopLai.error) : undefined;
  const quaLon = tep.some((f) => f.size > TRAN_MOI_TEP);

  return (
    <Dialog open={open} onClose={onClose} title={t('thamDinh.raSoat.nopLai')}>
      <div className="space-y-4 pt-2">
        <p className="text-sm text-content-muted">{t('thamDinh.raSoat.nopLaiMo')}</p>

        {loiChung && (
          <p className="rounded border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">{loiChung}</p>
        )}

        <Field label={t('thamDinh.nop.tieuDeHoSo')} required error={loiTruong['/tieu_de']}>
          {(p) => <Input {...p} value={tieuDe} onChange={(e) => setTieuDe(e.target.value)} />}
        </Field>

        <Field label={t('thamDinh.nop.tep')} required hint={t('thamDinh.nop.tepMo', { duoi: DUOI_NHAN.join(', ') })}>
          {() => <ChonTepHoSo tep={tep} onDoi={setTep} />}
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="md" onClick={onClose}>{t('chung.huy')}</Button>
          <Button
            variant="primary"
            size="md"
            loading={nopLai.isPending}
            disabled={!tieuDe.trim() || tep.length === 0 || quaLon}
            onClick={() => nopLai.mutate({ id: phienId, tieuDe: tieuDe.trim(), tep },
              { onSuccess: (p) => { onClose(); onXong?.(p.id); } })}
          >
            {nopLai.isPending ? t('thamDinh.nop.dangNop') : t('thamDinh.raSoat.nopLai')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
